import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AtSymbolIcon, CalendarIcon, DocumentAddIcon, UserIcon } from "@heroicons/react/solid"
import useMountStatus from "../../../../hooks/useMountStatus";

import { INVALID_ASSESSMENT, INVALID_EMAIL, NO_ACCESS_TYPES, PERMISSIONS, TIME_ENDOFTHEDAY } from "../../../../utils/constants";
import { assignWorkflowToUser, getOrganisationUsers, getOrganisationWorkflows } from "../../../../utils/api";

import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import Button from "../../../SharedComponents/Button";
import Input from "../../../Auth/Input";
import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import AccessControl from "../../../SharedComponents/AccessControl";

function AssignAssessment() {
    const [workflows, setWorkflows] = useState([]);
    const [workflow, setWorkflow] = useState();
    const [organisationUsers, setOrganisationUsers] = useState([]);
    const [existingUserSetup, setExistingUserSetup] = useState(false);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [dueDate, setDueDate] = useState(null);
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loadingAssessments, setLoadingAssessments] = useState(false);
    const [loadingOrganisationUsers, setLoadingOrganisationUsers] = useState(false);

    const unmounted = useMountStatus();

    const location = useLocation();

    const handleExistingUserSetupChange = async () => {
        setEmail("");

        if (existingUserSetup) {
            setExistingUserSetup(false);
        } else {
            setExistingUserSetup(true);
        }
    };

    const handleAssignAssessment = async () => {
        try {
            if (!email || email.length === 0) {
                setAlertType(ALERT_TYPE.ERROR);
                setAlert(INVALID_EMAIL);
                return;
            }

            if (!workflow || workflow.length === 0) {
                setAlertType(ALERT_TYPE.ERROR);
                setAlert(INVALID_ASSESSMENT);
                return;
            }

            setSubmitLoading(true);

            await assignWorkflowToUser(
                workflow,
                email,
                dueDate ? dueDate + TIME_ENDOFTHEDAY : null,
                null,
                firstName ? firstName : "",
                lastName ? lastName : ""
            );

            setAlertType(ALERT_TYPE.SUCCESS);
            setAlert(`Successfully assigned assessment to user ${email}`)

            setSubmitLoading(false);
        } catch (e) {
            setSubmitLoading(false);

            setAlertType(ALERT_TYPE.ERROR);
            setAlert(e.message);
        }
    }

    const fetchOrganisationUsers = useCallback(async () => {
        try {
            setLoadingOrganisationUsers(true);

            const organisationUsers = await getOrganisationUsers();

            if (unmounted) return;

            setOrganisationUsers(
                location.state ?
                    organisationUsers.filter(i => i.emailAddress !== location.state.user.emailAddress)
                    :
                    organisationUsers
            )
        } catch (err) {
            if (unmounted) return;
        }
        setLoadingOrganisationUsers(false);

    }, [location.state, unmounted]);


    const fetchData = useCallback(async () => {
        try {
            setLoadingAssessments(true);

            const workflows = await getOrganisationWorkflows();

            if (unmounted) return;

            setWorkflows(workflows);

            setLoadingAssessments(false);
        } catch (err) {
            if (unmounted) return;
            setLoadingAssessments(false);
        }
    }, [unmounted]);

    useEffect(() => {
        fetchData();
        fetchOrganisationUsers();

        if (location.state && !unmounted) {
            setEmail(location.state.user.emailAddress);
            setExistingUserSetup(true);
        }
    }, [fetchData, fetchOrganisationUsers, location.state, unmounted]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.ASSIGN_ASSESSMENTS} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <DashboardContainer
                title={"Assign Assessment"}
                titleIcon={<DocumentAddIcon className="block h-5 w-5 mr-2"/>}
            >
                <div className="grid grid-flow-row gap-4">
                    <div className="md:col-span-1">
                        <div>
                            <h3 className="text-md font-medium leading-6 text-indigo-900">
                                User and assessment details
                            </h3>
                            <p className="text-sm text-gray-600">
                                Please complete the form below to assign an assessment
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <form action="#" method="POST">
                            <Alert
                                text={alert}
                                type={alertType}
                                active={alert}
                                close={() => setAlert("")}
                            />
                            <div className="shadow-sm overflow-hidden rounded-md">
                                <div className="grid grid-flow-row gap-4 md:gap-6 p-4 bg-white">
                                    <div className={"grid grid-flow-row md:grid-cols-6 gap-4 md:gap-6"}>
                                        <div className="col-span-3">

                                            {existingUserSetup ?
                                                <>
                                                    <label
                                                        htmlFor="first-name"
                                                        className="block text-sm font-medium text-indigo-900"
                                                    >
                                                        User
                                                    </label>
                                                    <select
                                                        id="organisationUsers"
                                                        name="organisationUsers"
                                                        className={
                                                            "mt-2 relative w-full bg-blue-gray-50 border " +
                                                            "border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 " +
                                                            "text-left focus:outline-none focus:ring-1 " +
                                                            "focus:ring-primary-600 focus:border-primary-600 text-sm"
                                                        }
                                                        onChange={e => setEmail(e.target.value)}
                                                    >

                                                        {location.state && location.state.user.emailAddress ?
                                                            <option value={location.state.user.emailAddress}>
                                                                {location.state.user.name}
                                                            </option>
                                                            : null
                                                        }

                                                        {loadingOrganisationUsers ?
                                                            <option value="" className={"italic"}>
                                                                Loading Users...
                                                            </option>
                                                            :
                                                            <option value="">Please select</option>
                                                        }

                                                        {organisationUsers.map((user, index) => (
                                                            <option key={index} value={user.emailAddress}
                                                                    className={"mt-1"}>
                                                                {user.name}
                                                            </option>
                                                        ))
                                                        }

                                                    </select>
                                                </>
                                                :
                                                <div className={"grid sm:grid-flow-row md:grid-flow-col gap-4"}>
                                                    <Input
                                                        optional
                                                        type="text"
                                                        name="First name"
                                                        id="firstname"
                                                        placeholder={"First name"}
                                                        Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                        onChange={setFirstName}
                                                    />
                                                    <Input
                                                        optional
                                                        type="text"
                                                        name="Last name"
                                                        id="lastname"
                                                        placeholder={"Last name"}
                                                        Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                        onChange={setLastName}
                                                    />
                                                </div>
                                            }

                                        </div>
                                        <div className="col-span-3">
                                            <label
                                                htmlFor="first-name"
                                                className="block text-sm font-medium text-indigo-900"
                                            >
                                                Assessment
                                            </label>
                                            <select
                                                id="assessments"
                                                name="assessments"
                                                className={
                                                    "mt-2 relative w-full bg-blue-gray-50 border border-gray-300 " +
                                                    "rounded-md shadow-sm pl-3 pr-10 py-2 text-left " +
                                                    "focus:outline-none focus:ring-1 focus:ring-primary-600 " +
                                                    "focus:border-primary-600 text-sm"
                                                }
                                                onChange={e => setWorkflow(e.target.value)}
                                            >

                                                {loadingAssessments ?
                                                    <option value="" className={"italic"}>
                                                        Loading Assessments...
                                                    </option>
                                                    :
                                                    <option value="">Please select</option>
                                                }

                                                {workflows.map((workflow, index) => (
                                                    <option
                                                        key={index}
                                                        className={"mt-1"}
                                                        value={workflow.workflowIdentifier}
                                                    >
                                                        {workflow.productName}
                                                    </option>
                                                ))
                                                }

                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid grid-flow-row md:grid-cols-6 gap-4 md:gap-6">
                                        <div className="col-span-3">
                                            <Input
                                                id="email"
                                                type="email"
                                                name="Email"
                                                placeholder="Enter email"
                                                onChange={setEmail}
                                                Icon={<AtSymbolIcon className="h-4 w-4 text-gray-500"/>}
                                                disabled={existingUserSetup ? 'disabled' : ''}
                                                value={email}
                                            />
                                        </div>
                                        <div className="col-span-3">
                                            <Input
                                                id="date"
                                                type="date"
                                                name="Due Date"
                                                placeholder="Enter Due Date"
                                                onChange={setDueDate}
                                                Icon={<CalendarIcon className="h-4 w-4 text-gray-500"/>}
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className={"mt-2 text-sm text-left font-medium"}>
                                        <span className={"text-primary-600 cursor-pointer hover:text-primary-700"}
                                              onClick={handleExistingUserSetupChange}>
                                            {existingUserSetup ? "Set up new user" : "Set up existing user"}
                                        </span>
                                    </div>
                                </div>
                                <div className={
                                    "p-4 bg-gradient-to-l from-primary-50 to-secondary-50 flex items-center " +
                                    "justify-end text-right"
                                }>
                                    <Button
                                        text="Assign Assessment"
                                        icon={<DocumentAddIcon className="block h-4 w-4 mr-2"/>}
                                        iconPositionLeft
                                        onClick={handleAssignAssessment}
                                        loading={submitLoading}
                                        extraStyling={
                                            "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                            "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                        }
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </DashboardContainer>
        </AccessControl>
    );
}

export default AssignAssessment;
